/**
 * Pull + merge desk-open@v1 into IndexedDB — plan/33.
 */

import {
  activeOnly,
  mergeById,
  type DeskOpenPoRow,
  type DeskOpenSoRow,
} from "@icraftx/shared/v1";
import { pullDeskOpen } from "@/shared/api/sync";
import {
  loadDeskOpenCache,
  saveDeskOpenCache,
  type DeskOpenCache,
  type DeskOpenWatermark,
} from "./idb";

function advanceWatermark(
  response: { lastUpdated: string | null; lastId: string | null },
  prior: DeskOpenWatermark,
): DeskOpenWatermark {
  if (!response.lastUpdated) return prior;
  return {
    lastUpdated: response.lastUpdated,
    lastId: response.lastId,
  };
}

export async function pullDeskOpenChannel(
  companyRef: string,
  opts?: { forceFull?: boolean },
): Promise<DeskOpenCache> {
  const cached = await loadDeskOpenCache(companyRef);
  const useSo =
    !opts?.forceFull && cached?.soWatermark.lastUpdated
      ? cached.soWatermark
      : null;
  const usePo =
    !opts?.forceFull && cached?.poWatermark.lastUpdated
      ? cached.poWatermark
      : null;

  const res = await pullDeskOpen(companyRef, {
    soSince: useSo?.lastUpdated ?? undefined,
    soSinceId: useSo?.lastId ?? undefined,
    poSince: usePo?.lastUpdated ?? undefined,
    poSinceId: usePo?.lastId ?? undefined,
  });

  const salesOrders = activeOnly(
    mergeById<DeskOpenSoRow>(
      useSo ? (cached?.salesOrders ?? []) : [],
      res.salesOrders.data,
    ),
  );
  const purchaseOrders = activeOnly(
    mergeById<DeskOpenPoRow>(
      usePo ? (cached?.purchaseOrders ?? []) : [],
      res.purchaseOrders.data,
    ),
  );

  const next: DeskOpenCache = {
    companyRef,
    salesOrders,
    purchaseOrders,
    soWatermark: advanceWatermark(
      res.salesOrders,
      useSo ?? { lastUpdated: null, lastId: null },
    ),
    poWatermark: advanceWatermark(
      res.purchaseOrders,
      usePo ?? { lastUpdated: null, lastId: null },
    ),
    savedAt: new Date().toISOString(),
  };
  await saveDeskOpenCache(next);
  return next;
}
